import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Checkbox,
  Button,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Card,
  CardHeader,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  ButtonGroup,
  ToggleButtonGroup,
  ToggleButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Stack,
  Grid,
  Tabs,
  Tab,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormGroup,
  FormControlLabel
} from '@mui/material';
import {
  Settings,
  ArrowDropUp,
  ArrowDropDown,
  Info,
  Edit,
  Delete,
  Visibility,
  ViewColumn,
  ExpandMore,
  BarChart,
  ShowChart,
  PriceCheck,
  Assessment,
  StackedLineChart,
  Shield,
  Category,
  CheckCircle,
  MoreVert,
  Close as CloseIcon
} from '@mui/icons-material';
import { TimeHorizon } from './StockList';
import { alpha } from '@mui/material/styles';

interface EnhancedWatchlistTableProps {
  title: string;
  subtitle?: string;
  stocks: any[];
  onTickerSelect: (ticker: string) => void;
  actions: any[];
  timeHorizon: TimeHorizon;
  onTimeHorizonChange: (horizon: TimeHorizon) => void;
  initialPresetView?: string;
  onPresetViewChange?: (presetView: string) => void;
}

// Define column groups for organization
const COLUMN_GROUPS = {
  BASIC: 'Basic Information',
  PRICE_TARGETS: 'Price Targets',
  PERFORMANCE: 'Performance',
  TECHNICAL: 'Technical Indicators',
  FUNDAMENTAL: 'Fundamentals',
  RATINGS: 'Ratings & Sentiment',
  RISK: 'Risk Metrics'
};

// Type for column padding to match Material UI's TableCell
type TableCellPadding = 'normal' | 'none' | 'checkbox';

// Interface for column configuration
interface Column {
  id: string;
  label: string;
  group: string;
  numeric: boolean;
  required?: boolean;
  format?: string;
  timeHorizon?: string;
  minWidth?: number;
  padding?: TableCellPadding;
}

// Map icons to column groups for visual cues
const GROUP_ICONS = {
  [COLUMN_GROUPS.BASIC]: <Category />,
  [COLUMN_GROUPS.PRICE_TARGETS]: <PriceCheck />,
  [COLUMN_GROUPS.PERFORMANCE]: <BarChart />,
  [COLUMN_GROUPS.TECHNICAL]: <ShowChart />,
  [COLUMN_GROUPS.FUNDAMENTAL]: <Assessment />,
  [COLUMN_GROUPS.RATINGS]: <StackedLineChart />,
  [COLUMN_GROUPS.RISK]: <Shield />
};

// Define all available columns 
const ALL_COLUMNS: Column[] = [
  { id: 'ticker', label: 'Ticker', group: COLUMN_GROUPS.BASIC, required: true, numeric: false, minWidth: 80, padding: 'normal' },
  { id: 'name', label: 'Name', group: COLUMN_GROUPS.BASIC, numeric: false, minWidth: 150, padding: 'normal' },
  { id: 'close', label: 'Price', group: COLUMN_GROUPS.BASIC, required: true, numeric: true, minWidth: 80, padding: 'normal' },
  { id: 'gain_last_1day', label: 'Daily Change %', group: COLUMN_GROUPS.PERFORMANCE, format: 'percent', numeric: true, minWidth: 120, padding: 'normal' },
  { id: 'volume', label: 'Volume', group: COLUMN_GROUPS.BASIC, format: 'number', numeric: true, minWidth: 100, padding: 'normal' },
  
  // Price targets based on time horizon
  { id: 'ShortTermPriceTarget', label: 'Short Target', group: COLUMN_GROUPS.PRICE_TARGETS, timeHorizon: 'Short Term', numeric: true, minWidth: 120, padding: 'normal' },
  { id: 'SwingTradePriceTarget', label: 'Swing Target', group: COLUMN_GROUPS.PRICE_TARGETS, timeHorizon: 'Mid Term', numeric: true, minWidth: 120, padding: 'normal' },
  { id: 'LongTermPriceTarget', label: 'Long Target', group: COLUMN_GROUPS.PRICE_TARGETS, timeHorizon: 'Long Term', numeric: true, minWidth: 120, padding: 'normal' },
  
  // Expected gains based on time horizon
  { id: 'ShortTermGain', label: 'ST Potential %', group: COLUMN_GROUPS.PRICE_TARGETS, format: 'percent', timeHorizon: 'Short Term', numeric: true, minWidth: 120, padding: 'normal' },
  { id: 'SwingTradeGain', label: 'MT Potential %', group: COLUMN_GROUPS.PRICE_TARGETS, format: 'percent', timeHorizon: 'Mid Term', numeric: true, minWidth: 120, padding: 'normal' },
  { id: 'LongTermGain', label: 'LT Potential %', group: COLUMN_GROUPS.PRICE_TARGETS, format: 'percent', timeHorizon: 'Long Term', numeric: true, minWidth: 120, padding: 'normal' },
  
  // Probabilities
  { id: 'ShortTermProbabilityOfHittingTarget', label: 'ST Probability', group: COLUMN_GROUPS.PRICE_TARGETS, format: 'percent', timeHorizon: 'Short Term', numeric: true, minWidth: 120, padding: 'normal' },
  { id: 'SwingTradeProbabilityOfHittingTarget', label: 'MT Probability', group: COLUMN_GROUPS.PRICE_TARGETS, format: 'percent', timeHorizon: 'Mid Term', numeric: true, minWidth: 120, padding: 'normal' },
  { id: 'LongTermProbabilityOfHittingTarget', label: 'LT Probability', group: COLUMN_GROUPS.PRICE_TARGETS, format: 'percent', timeHorizon: 'Long Term', numeric: true, minWidth: 120, padding: 'normal' },
  
  // Performance metrics
  { id: 'gain_last_2weeks', label: '2 Week %', group: COLUMN_GROUPS.PERFORMANCE, format: 'percent', numeric: true, minWidth: 100, padding: 'normal' },
  { id: 'gain_last_1month', label: '1 Month %', group: COLUMN_GROUPS.PERFORMANCE, format: 'percent', numeric: true, minWidth: 100, padding: 'normal' },
  { id: 'gain_last_6months', label: '6 Month %', group: COLUMN_GROUPS.PERFORMANCE, format: 'percent', numeric: true, minWidth: 100, padding: 'normal' },
  
  // Technical indicators
  { id: 'EMA_20', label: 'EMA 20', group: COLUMN_GROUPS.TECHNICAL, numeric: true, minWidth: 90, padding: 'normal' },
  { id: 'EMA_50', label: 'EMA 50', group: COLUMN_GROUPS.TECHNICAL, numeric: true, minWidth: 90, padding: 'normal' },
  { id: 'EMA_200', label: 'EMA 200', group: COLUMN_GROUPS.TECHNICAL, numeric: true, minWidth: 90, padding: 'normal' },
  
  // Ratings
  { id: 'ShortTermRating', label: 'ST Rating', group: COLUMN_GROUPS.RATINGS, timeHorizon: 'Short Term', numeric: false, minWidth: 100, padding: 'normal' },
  { id: 'SwingTradeRating', label: 'MT Rating', group: COLUMN_GROUPS.RATINGS, timeHorizon: 'Mid Term', numeric: false, minWidth: 100, padding: 'normal' },
  { id: 'LongTermRating', label: 'LT Rating', group: COLUMN_GROUPS.RATINGS, timeHorizon: 'Long Term', numeric: false, minWidth: 100, padding: 'normal' },
  { id: 'Sentiment', label: 'Sentiment', group: COLUMN_GROUPS.RATINGS, numeric: false, minWidth: 100, padding: 'normal' },
  
  // Fundamentals
  { id: 'eps', label: 'EPS', group: COLUMN_GROUPS.FUNDAMENTAL, numeric: true, minWidth: 80, padding: 'normal' },
  { id: 'pe_ratio', label: 'P/E Ratio', group: COLUMN_GROUPS.FUNDAMENTAL, numeric: true, minWidth: 90, padding: 'normal' },
  { id: 'divident', label: 'Dividend', group: COLUMN_GROUPS.FUNDAMENTAL, numeric: true, minWidth: 90, padding: 'normal' },
  { id: 'revenue_yoy', label: 'Revenue YoY %', group: COLUMN_GROUPS.FUNDAMENTAL, format: 'percent', numeric: true, minWidth: 120, padding: 'normal' },
  { id: 'eps_yoy', label: 'EPS YoY %', group: COLUMN_GROUPS.FUNDAMENTAL, format: 'percent', numeric: true, minWidth: 120, padding: 'normal' },
  { id: 'net_margin', label: 'Net Margin %', group: COLUMN_GROUPS.FUNDAMENTAL, format: 'percent', numeric: true, minWidth: 120, padding: 'normal' },
  
  // Risk metrics
  { id: 'ShortTerm_sharpe_ratio', label: 'ST Sharpe', group: COLUMN_GROUPS.RISK, timeHorizon: 'Short Term', numeric: true, minWidth: 100, padding: 'normal' },
  { id: 'SwingTrade_sharpe_ratio', label: 'MT Sharpe', group: COLUMN_GROUPS.RISK, timeHorizon: 'Mid Term', numeric: true, minWidth: 100, padding: 'normal' },
  { id: 'LongTerm_sharpe_ratio', label: 'LT Sharpe', group: COLUMN_GROUPS.RISK, timeHorizon: 'Long Term', numeric: true, minWidth: 100, padding: 'normal' },
  { id: 'volatility_last_1month', label: 'Volatility', group: COLUMN_GROUPS.RISK, format: 'percent', numeric: true, minWidth: 100, padding: 'normal' },
];

// Define preset column configurations for different user needs
const PRESET_VIEWS = {
  DEFAULT: 'Default View',
  PRICE_TARGETS: 'Price Targets',
  TECHNICAL: 'Technical Analysis',
  FUNDAMENTAL: 'Fundamental Analysis',
  PERFORMANCE: 'Performance View',
  RISK: 'Risk Analysis'
};

// Descriptions for each preset view to help users understand them
const PRESET_DESCRIPTIONS = {
  [PRESET_VIEWS.DEFAULT]: 'Basic stock information including ticker, price, daily change, and volume.',
  [PRESET_VIEWS.PRICE_TARGETS]: 'Price targets and potential gains across different time horizons.',
  [PRESET_VIEWS.TECHNICAL]: 'Technical analysis indicators like moving averages and price patterns.',
  [PRESET_VIEWS.FUNDAMENTAL]: 'Financial metrics like EPS, revenue growth, and profit margins.',
  [PRESET_VIEWS.PERFORMANCE]: 'Historical price performance over various time periods.',
  [PRESET_VIEWS.RISK]: 'Risk metrics like volatility and Sharpe ratios for risk-adjusted returns.'
};

// Define which columns to show in each preset
const PRESET_COLUMNS = {
  [PRESET_VIEWS.DEFAULT]: ['ticker', 'close', 'gain_last_1day', 'volume'],
  [PRESET_VIEWS.PRICE_TARGETS]: ['ticker', 'close', 'ShortTermPriceTarget', 'SwingTradePriceTarget', 'LongTermPriceTarget', 'ShortTermGain', 'SwingTradeGain', 'LongTermGain'],
  [PRESET_VIEWS.TECHNICAL]: ['ticker', 'close', 'gain_last_1day', 'EMA_20', 'EMA_50', 'EMA_200', 'volume'],
  [PRESET_VIEWS.FUNDAMENTAL]: ['ticker', 'name', 'close', 'eps', 'pe_ratio', 'revenue_yoy', 'eps_yoy', 'net_margin', 'divident'],
  [PRESET_VIEWS.PERFORMANCE]: ['ticker', 'close', 'gain_last_1day', 'gain_last_2weeks', 'gain_last_1month', 'gain_last_6months'],
  [PRESET_VIEWS.RISK]: ['ticker', 'close', 'ShortTerm_sharpe_ratio', 'SwingTrade_sharpe_ratio', 'LongTerm_sharpe_ratio', 'volatility_last_1month'],
};

export const EnhancedWatchlistTable: React.FC<EnhancedWatchlistTableProps> = ({
  title,
  subtitle,
  stocks,
  onTickerSelect,
  actions,
  timeHorizon,
  onTimeHorizonChange,
  initialPresetView = 'Default View',
  onPresetViewChange = () => {}
}) => {
  // Initialize with the saved preset if available
  const [currentPreset, setCurrentPreset] = useState<string>(initialPresetView);
  const [activeColumns, setActiveColumns] = useState<string[]>(
    PRESET_COLUMNS[initialPresetView] || PRESET_COLUMNS['Default View']
  );
  const [isColumnDialogOpen, setIsColumnDialogOpen] = useState(false);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
  const [dialogTab, setDialogTab] = useState(0);
  const [selectedView, setSelectedView] = useState<string>(initialPresetView);

  // Extract watchlist-level actions
  const editAction = actions.find(action => action.label === 'Edit');
  const deleteAction = actions.find(action => action.label === 'Delete');

  // Apply sorting to the stock data
  const sortedStocks = React.useMemo(() => {
    if (!sortConfig) return stocks;
    
    return [...stocks].sort((a, b) => {
      // Handle undefined values
      if (a[sortConfig.key] === undefined) return sortConfig.direction === 'asc' ? -1 : 1;
      if (b[sortConfig.key] === undefined) return sortConfig.direction === 'asc' ? 1 : -1;
      
      // Sort logic based on value types
      if (typeof a[sortConfig.key] === 'string') {
        return sortConfig.direction === 'asc' 
          ? a[sortConfig.key].localeCompare(b[sortConfig.key])
          : b[sortConfig.key].localeCompare(a[sortConfig.key]);
      }
      
      return sortConfig.direction === 'asc' 
        ? a[sortConfig.key] - b[sortConfig.key] 
        : b[sortConfig.key] - a[sortConfig.key];
    });
  }, [stocks, sortConfig]);

  // Handle sort request when column header is clicked
  const requestSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Toggle column selection
  const toggleColumn = (columnId: string) => {
    if (columnId === 'ticker') return; // Can't remove ticker

    setActiveColumns(prevColumns => {
      // If column is already active, remove it
      if (prevColumns.includes(columnId)) {
        return prevColumns.filter(id => id !== columnId);
      }
      // Otherwise add it
      return [...prevColumns, columnId];
    });
    
    // When custom columns are selected, we're no longer using a preset
    setCurrentPreset('Custom');
  };

  // Format cell values based on their type
  const formatCellValue = (value: any, format?: string) => {
    if (value === undefined || value === null) return '-';
    
    switch (format) {
      case 'percent':
        return `${(value).toFixed(2)}%`;
      case 'number':
        return value.toLocaleString();
      default:
        return typeof value === 'number' ? value.toFixed(2) : value;
    }
  };

  // Get the CSS class for cells that represent changes (to color green/red)
  const getChangeCellClass = (value: number) => {
    // Use MUI's sx prop instead of CSS class
    return {
      sx: {
        color: value > 0 ? 'success.main' : value < 0 ? 'error.main' : 'text.primary'
      }
    };
  };

  // Apply a preset configuration and notify parent component
  const applyPreset = (preset: string) => {
    if (PRESET_COLUMNS[preset]) {
      setActiveColumns(PRESET_COLUMNS[preset]);
      setCurrentPreset(preset);
      setSelectedView(preset);
      onPresetViewChange(preset);
    }
  };

  // Use useEffect to handle initialPresetView changes (if the prop changes)
  useEffect(() => {
    if (initialPresetView && initialPresetView !== currentPreset) {
      setCurrentPreset(initialPresetView);
      if (PRESET_COLUMNS[initialPresetView]) {
        setActiveColumns(PRESET_COLUMNS[initialPresetView]);
      }
    }
  }, [initialPresetView]);

  // Filter columns by their group for the column selection menu
  const getColumnsByGroup = (group: string): Column[] => {
    return ALL_COLUMNS.filter(column => column.group === group);
  };

  // Get visible columns based on current time horizon
  const getVisibleColumns = (): Column[] => {
    return ALL_COLUMNS.filter(column => 
      activeColumns.includes(column.id) && 
      (!column.timeHorizon || column.timeHorizon === timeHorizon)
    );
  };

  const handleOpenColumnDialog = () => {
    setIsColumnDialogOpen(true);
  };

  const handleCloseColumnDialog = () => {
    setIsColumnDialogOpen(false);
  };

  return (
    <Card sx={{ mb: 3 }}>
      {/* Redesigned card header with better control organization */}
      <Box sx={{ 
        p: 2, 
        display: 'flex', 
        flexDirection: 'column',
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        {/* Title row with subtitle */}
        <Box sx={{ mb: subtitle ? 0.5 : 2 }}>
          <Typography variant="h6">
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        
        {/* Controls row */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          {/* Left side: Time horizon selector */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
              Time Horizon:
            </Typography>
            <ButtonGroup size="small" variant="outlined">
              <Button 
                onClick={() => onTimeHorizonChange('Short Term')} 
                variant={timeHorizon === 'Short Term' ? 'contained' : 'outlined'}
                color="primary"
                sx={{ px: 1 }}
              >
                Short
              </Button>
              <Button 
                onClick={() => onTimeHorizonChange('Mid Term')} 
                variant={timeHorizon === 'Mid Term' ? 'contained' : 'outlined'}
                color="primary"
                sx={{ px: 1 }}
              >
                Mid
              </Button>
              <Button 
                onClick={() => onTimeHorizonChange('Long Term')} 
                variant={timeHorizon === 'Long Term' ? 'contained' : 'outlined'}
                color="primary"
                sx={{ px: 1 }}
              >
                Long
              </Button>
            </ButtonGroup>
          </Box>
          
          {/* Right side: Action buttons */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            {/* Customize view button with text */}
            <Button
              size="small"
              startIcon={<ViewColumn fontSize="small" />}
              onClick={handleOpenColumnDialog}
              variant="text"
              sx={{
                '&:hover': {
                  backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04)
                }
              }}
            >
              Customize View
            </Button>
            
            {/* Direct action buttons instead of dropdown menu */}
            {editAction && (
              <Tooltip title="Edit Watchlist">
                <IconButton 
                  size="small" 
                  onClick={editAction.onClick}
                  sx={{ 
                    height: 32,
                    width: 32,
                    '&:hover': {
                      backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04)
                    }
                  }}
                >
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            
            {deleteAction && (
              <Tooltip title="Delete Watchlist">
                <IconButton 
                  size="small" 
                  color="error" 
                  onClick={deleteAction.onClick}
                  sx={{ 
                    height: 32,
                    width: 32,
                    '&:hover': {
                      backgroundColor: (theme) => alpha(theme.palette.error.main, 0.04)
                    }
                  }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Box>

      {/* The main table */}
      <CardContent sx={{ p: 0 }}>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow sx={{ 
                bgcolor: (theme) => 
                  theme.palette.mode === 'dark' 
                    ? '#1A2542' // colors.ui.backgroundDarkPaper
                    : '#F5F7FA' // colors.ui.backgroundLight
              }}>
                {getVisibleColumns().map((column) => (
                  <TableCell 
                    key={column.id}
                    onClick={() => requestSort(column.id)}
                    align="left"
                    sx={{ 
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      bgcolor: sortConfig?.key === column.id ? 'action.selected' : 'inherit',
                      padding: '8px 16px',
                      transition: 'background-color 0.2s ease'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                      <span>{column.label}</span>
                      {sortConfig?.key === column.id && (
                        sortConfig.direction === 'asc' 
                          ? <ArrowDropUp /> 
                          : <ArrowDropDown />
                      )}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedStocks.length > 0 ? (
                sortedStocks.map((stock, index) => (
                  <TableRow 
                    key={`${stock.ticker}-${index}`}
                    hover
                    onClick={() => onTickerSelect(stock.ticker)}
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04)
                      }
                    }}
                  >
                    {getVisibleColumns().map((column) => {
                      const value = stock[column.id];
                      const isChangeColumn = column.id.includes('gain_') || 
                                            column.id.includes('Gain') || 
                                            column.id.includes('_yoy');
                      
                      return (
                        <TableCell 
                          key={column.id} 
                          align="left"
                          padding={column.padding || 'normal'}
                          sx={{
                            ...(isChangeColumn && getChangeCellClass(value).sx),
                            minWidth: column.minWidth || 'auto'
                          }}
                        >
                          {formatCellValue(value, column.format)}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={getVisibleColumns().length} align="left">
                    No stocks found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>

      {/* New comprehensive column dialog */}
      <Dialog
        open={isColumnDialogOpen}
        onClose={handleCloseColumnDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Customize Data View
          <IconButton
            aria-label="close"
            onClick={handleCloseColumnDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: (theme) => 
                  theme.palette.mode === 'dark' ? alpha(theme.palette.info.main, 0.1) : alpha(theme.palette.primary.main, 0.1),
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              You can choose a preset data view or customize your own set of columns to display.
            </Alert>
            
            <Tabs 
              value={dialogTab} 
              onChange={(e, newValue) => setDialogTab(newValue)}
              sx={{ 
                mb: 2, 
                borderBottom: 1, 
                borderColor: 'divider',
                '& .MuiTab-root': {
                  color: 'text.secondary',
                  '&.Mui-selected': {
                    color: (theme) => theme.palette.mode === 'dark' ? theme.palette.info.main : theme.palette.primary.main
                  }
                }
              }}
            >
              <Tab label="Preset Views" />
              <Tab label="Custom Columns" />
            </Tabs>
            
            {/* Preset Views Tab */}
            {dialogTab === 0 && (
              <Grid container spacing={2}>
                {Object.entries(PRESET_VIEWS).map(([key, viewName]) => (
                  <Grid item xs={12} md={6} key={key}>
                    <Card 
                      variant="outlined" 
                      sx={{ 
                        cursor: 'pointer',
                        border: selectedView === viewName ? '2px solid' : '1px solid',
                        borderColor: selectedView === viewName ? 'primary.main' : 'divider',
                        boxShadow: selectedView === viewName ? 2 : 0,
                        height: '100%',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          boxShadow: (theme) => `0px 4px 8px ${alpha(theme.palette.primary.main, 0.1)}`
                        }
                      }}
                      onClick={() => {
                        setSelectedView(viewName);
                      }}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          {selectedView === viewName && (
                            <CheckCircle color="primary" sx={{ mr: 1 }} />
                          )}
                          <Typography variant="h6">{viewName}</Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {PRESET_DESCRIPTIONS[viewName]}
                        </Typography>
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            Includes columns:
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                            {PRESET_COLUMNS[viewName].map(colId => {
                              const column = ALL_COLUMNS.find(col => col.id === colId);
                              return column ? (
                                <Chip 
                                  key={colId} 
                                  label={column.label} 
                                  size="small" 
                                  variant="outlined"
                                  sx={{
                                    borderColor: (theme) => 
                                      theme.palette.mode === 'dark' ? alpha(theme.palette.info.main, 0.5) : alpha(theme.palette.primary.main, 0.5),
                                    color: 'text.primary',
                                    '&:hover': {
                                      backgroundColor: (theme) => 
                                        theme.palette.mode === 'dark' ? alpha(theme.palette.info.main, 0.1) : alpha(theme.palette.primary.main, 0.1),
                                    }
                                  }}
                                />
                              ) : null;
                            })}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
            
            {/* Custom Columns Tab */}
            {dialogTab === 1 && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Select specific columns to display from each category. This will create a custom view.
                </Typography>
                
                {Object.entries(COLUMN_GROUPS).map(([key, groupName]) => (
                  <Accordion key={key} defaultExpanded={groupName === COLUMN_GROUPS.BASIC}>
                    <AccordionSummary 
                      expandIcon={<ExpandMore />}
                      sx={{
                        '&:hover': {
                          backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04)
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ 
                          color: (theme) => 
                            theme.palette.mode === 'dark' ? theme.palette.info.main : theme.palette.primary.main
                        }}>
                          {GROUP_ICONS[groupName]}
                        </Box>
                        <Typography sx={{ ml: 1 }}>{groupName}</Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      <FormGroup>
                        <Grid container spacing={1}>
                          {getColumnsByGroup(groupName).map(column => (
                            <Grid item xs={12} sm={6} key={column.id}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={activeColumns.includes(column.id)}
                                    onChange={() => toggleColumn(column.id)}
                                    disabled={column.required}
                                    color="primary"
                                    size="small"
                                  />
                                }
                                label={column.label}
                                sx={{ 
                                  '& .MuiFormControlLabel-label': { 
                                    fontSize: '0.875rem',
                                    color: column.required ? 'text.secondary' : 'text.primary'
                                  },
                                  '&:hover': {
                                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
                                    borderRadius: 1
                                  },
                                  py: 0.5,
                                  px: 1,
                                  width: '100%'
                                }}
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </FormGroup>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseColumnDialog} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={() => {
              if (dialogTab === 0 && selectedView) {
                applyPreset(selectedView);
              }
              handleCloseColumnDialog();
            }} 
            color="primary" 
            variant="contained"
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}; 